import React, { useState } from 'react';
import axios from 'axios';
import './App.css';
import { sendPayment, saveSplit } from './services/dashService';
import dashLogo from './dashlogo.png';
import dashCoin from './dashcoin.png';
import voucherImg from './voucher.png';
import splitBillImg from './splitbill.png';
import saveIcon from './save.png';
import scanQRIcon from './scan.png';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://dashsplit-backend.up.railway.app';
const MAX_PARTICIPANTS = 6;
const CONTRACT_EXPLORER = 'https://testnet.platform-explorer.com/identity/AuZz4KUirh5XJJZpyvzvzY3gnf9i1t1yyAKysnRvuyah';
const MOCK_DASH_ADDRESS = 'yMkPsVPBQeEBWwSsUBMXTv3hVgAmBkTfx3';

// ═══════════════════════════════════════════════════════════════════
// UNIFIED DASHCOIN SYSTEM
// ═══════════════════════════════════════════════════════════════════
const GARDEN_STAGES = [
  { name: 'Seed',          min: 0,   petals: 0, color: null,      leafL: false, leafR: false, centerR: 0,  desc: 'Earn DashCoins to sprout your garden' },
  { name: 'Sprout',        min: 20,  petals: 0, color: '#A5D6A7', leafL: false, leafR: false, centerR: 0,  desc: 'Your garden is waking up!' },
  { name: 'First Petal',   min: 50,  petals: 1, color: '#FF7043', leafL: true,  leafR: false, centerR: 6,  desc: 'Your first petal has bloomed!' },
  { name: 'Growing',       min: 75,  petals: 2, color: '#FF7043', leafL: true,  leafR: false, centerR: 7,  desc: 'Two petals and growing!' },
  { name: 'Budding',       min: 100, petals: 3, color: '#FF9800', leafL: true,  leafR: true,  centerR: 9,  desc: 'More petals opening every split!' },
  { name: 'Blooming',      min: 120, petals: 4, color: '#E91E63', leafL: true,  leafR: true,  centerR: 10, desc: 'Halfway to full bloom — beautiful!' },
  { name: 'Lush',          min: 140, petals: 5, color: '#E91E63', leafL: true,  leafR: true,  centerR: 11, desc: 'Your garden is flourishing!' },
  { name: 'Full Bloom',    min: 160, petals: 6, color: '#9C27B0', leafL: true,  leafR: true,  centerR: 12, desc: 'A stunning full flower!' },
  { name: 'Grand Bloom',   min: 200, petals: 7, color: '#9C27B0', leafL: true,  leafR: true,  centerR: 13, desc: 'Almost at legendary status!' },
  { name: 'Golden Orchid', min: 240, petals: 8, color: '#FFD700', leafL: true,  leafR: true,  centerR: 14, desc: 'Legendary! Your garden is golden.' },
];

const THEMES = {
  default:  { heroBg: 'linear-gradient(180deg, #0C44AC 0%, #1BA2F6 100%)', heroShadow: '0 8px 30px rgba(27,162,246,0.3)', bg: '#F0F9FF', border: '#BFDBFE', name: 'Sky Garden',    icon: '☀️' },
  sunset:   { heroBg: 'linear-gradient(180deg, #C2410C 0%, #FB923C 100%)', heroShadow: '0 8px 30px rgba(251,146,60,0.35)', bg: '#FFF7ED', border: '#FED7AA', name: 'Sunset Meadow', icon: '🌅' },
  midnight: { heroBg: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)', heroShadow: '0 8px 30px rgba(0,0,0,0.5)',      bg: '#1E293B', border: '#334155', name: 'Night Grove',   icon: '🌑', dark: true },
};

const THEME_VOUCHERS = [
  { id: 101, name: 'Sunset Meadow Theme', cost: 50,  icon: '🌅', themeKey: 'sunset'   },
  { id: 102, name: 'Midnight Grove Theme', cost: 100, icon: '🌑', themeKey: 'midnight' },
];

const VOUCHERS = [
  { id: 1, restaurant: 'Burger Lab',           deal: '10% off your next bill',    cost: 10, icon: '🍔', color: '#FFF7ED', border: '#FED7AA', tag: 'Popular' },
  { id: 2, restaurant: 'OldTown White Coffee',  deal: 'Free drink with any meal',  cost: 8,  icon: '☕', color: '#FFF1F2', border: '#FECDD3', tag: 'Hot Deal' },
  { id: 3, restaurant: "Nando's Malaysia",      deal: 'Free quarter chicken',      cost: 15, icon: '🍗', color: '#FFF7ED', border: '#FED7AA', tag: null },
  { id: 4, restaurant: 'Sushi King',            deal: 'Buy 5 plates, get 2 free',  cost: 12, icon: '🍣', color: '#F0F9FF', border: '#BAE6FD', tag: 'New' },
  { id: 5, restaurant: 'Marrybrown',            deal: 'Free upsize on combo meal', cost: 6,  icon: '🍟', color: '#F0FDF4', border: '#BBF7D0', tag: null },
  { id: 6, restaurant: 'The Ship Restaurant',   deal: 'Complimentary dessert',     cost: 20, icon: '🍰', color: '#FAF5FF', border: '#E9D5FF', tag: 'Premium' },
];

const INITIAL_MOCK_COINS = 14;
const MOCK_WALLET_BALANCE = 12.50;

function loadCoins() { return Number(localStorage.getItem('dash_coins') ?? INITIAL_MOCK_COINS); }
function saveCoins(n) { localStorage.setItem('dash_coins', n); }
function loadWallet() { return Number(localStorage.getItem('dash_wallet') ?? MOCK_WALLET_BALANCE); }
function saveWallet(n) { localStorage.setItem('dash_wallet', n); }

function getStage(coins) {
  for (let i = GARDEN_STAGES.length - 1; i >= 0; i--) {
    if (coins >= GARDEN_STAGES[i].min) return GARDEN_STAGES[i];
  }
  return GARDEN_STAGES[0];
}

function getStageIndex(coins) {
  for (let i = GARDEN_STAGES.length - 1; i >= 0; i--) {
    if (coins >= GARDEN_STAGES[i].min) return i;
  }
  return 0;
}

// ═══════════════════════════════════════════════════════════════════
// FLOWER GARDEN SVG
// ═══════════════════════════════════════════════════════════════════
function FlowerSVG({ coins, size = 120 }) {
  const si = getStageIndex(coins);
  const stage = GARDEN_STAGES[si];

  const stemProgress = coins < 20 ? 0 : Math.min(1, (coins - 20) / 80);
  const STEM_BASE_Y = 148;
  const STEM_TOP_Y = 88;
  const stemTopY = STEM_BASE_Y - stemProgress * (STEM_BASE_Y - STEM_TOP_Y);
  const FCX = 80;
  const FCY = stemTopY;
  const petalColor = stage.color || '#FF7043';
  const isGolden = stage.name === 'Golden Orchid';
  const petalRX = coins >= 160 ? 9 : 8;
  const petalRY = coins >= 160 ? 17 : coins >= 120 ? 15 : 13;
  const petalDist = petalRY - 2;
  const PETAL_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

  return (
    <svg width={size} height={size} viewBox="0 0 160 160" style={{ display: 'block', overflow: 'visible' }} xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="80" cy="155" rx="46" ry="7" fill="#8D6E63" opacity="0.28" />
      {coins >= 20 && (
        <path d={`M80 150 Q${74 + stemProgress * 3} ${STEM_BASE_Y - (STEM_BASE_Y - stemTopY) * 0.5} 80 ${stemTopY}`}
          stroke="#4CAF50" strokeWidth="3.5" fill="none" strokeLinecap="round" style={{ transition: 'all 0.8s ease' }} />
      )}
      {coins < 20 && <ellipse cx="80" cy="148" rx="7" ry="4" fill="#795548" />}
      {stage.leafL && (
        <path d={`M80 ${stemTopY + 28} Q58 ${stemTopY + 16} 54 ${stemTopY + 4} Q68 ${stemTopY + 6} 80 ${stemTopY + 28}Z`}
          fill="#66BB6A" style={{ animation: 'leafSway 3.5s ease-in-out infinite alternate' }} />
      )}
      {stage.leafR && (
        <path d={`M80 ${stemTopY + 16} Q102 ${stemTopY + 4} 106 ${stemTopY - 8} Q92 ${stemTopY - 4} 80 ${stemTopY + 16}Z`}
          fill="#4CAF50" style={{ animation: 'leafSwayR 4s ease-in-out infinite alternate' }} />
      )}
      <g style={{ transformOrigin: `${FCX}px ${STEM_BASE_Y}px`, animation: coins >= 20 ? 'flowerSway 3s ease-in-out infinite alternate' : 'none' }}>
        {PETAL_ANGLES.slice(0, stage.petals).map((angleDeg, i) => (
          <g key={i} transform={`rotate(${angleDeg}, ${FCX}, ${FCY})`}>
            <ellipse cx={FCX} cy={FCY - petalDist} rx={petalRX} ry={petalRY} fill={petalColor} opacity={0.88} />
          </g>
        ))}
        {stage.centerR > 0 && (
          <>
            <circle cx={FCX} cy={FCY} r={stage.centerR + 2} fill={isGolden ? '#FF8F00' : '#F57F17'} />
            <circle cx={FCX} cy={FCY} r={stage.centerR} fill={isGolden ? '#FFD700' : '#FBC02D'} />
          </>
        )}
        {isGolden && [[-26, -26], [26, -26], [-30, 4], [30, 4], [0, -34], [-18, 22], [18, 22]].map(([dx, dy], i) => (
          <circle key={i} cx={FCX + dx} cy={FCY + dy} r={i % 2 === 0 ? 2.5 : 1.8} fill="#FFD700"
            style={{ animation: `sparkle ${1.2 + i * 0.25}s ease-in-out infinite alternate` }} />
        ))}
      </g>
    </svg>
  );
}

// Full garden widget (standalone use in voucher page etc)
function FlowerGarden({ coins, themeKey = 'default' }) {
  const theme = THEMES[themeKey] || THEMES.default;
  const isDark = theme.dark;
  const si = getStageIndex(coins);
  const stage = GARDEN_STAGES[si];
  const nextStage = GARDEN_STAGES[Math.min(si + 1, GARDEN_STAGES.length - 1)];
  const isMax = si >= GARDEN_STAGES.length - 1;
  const progress = isMax ? 100 : Math.round(((coins - stage.min) / (nextStage.min - stage.min)) * 100);
  const isGolden = stage.name === 'Golden Orchid';
  const labelColor = isDark ? '#E2E8F0' : '#1E293B';
  const subTextColor = '#94A3B8';

  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 20, padding: '18px 20px', marginBottom: 16, textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#CBD5E1' : '#64748B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>🌸 Financial Garden</div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
        <FlowerSVG coins={coins} size={130} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: labelColor, marginBottom: 2 }}>{stage.name}</div>
      <div style={{ fontSize: 12, color: subTextColor, marginBottom: 10 }}>{stage.desc}</div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg, #1BA2F6, #0C44AC)', color: 'white', borderRadius: 999, padding: '4px 14px', fontWeight: 700, fontSize: 14, marginBottom: 12, boxShadow: '0 2px 8px rgba(27,162,246,0.3)' }}>
        🪙 {coins} DC
      </div>
      <div style={{ width: '100%', height: 8, background: isDark ? '#334155' : '#E2E8F0', borderRadius: 10, marginBottom: 6, overflow: 'hidden' }}>
        <div style={{ width: `${progress}%`, height: '100%', background: isGolden ? 'linear-gradient(90deg, #FFD700, #FF8F00)' : 'linear-gradient(90deg, #1BA2F6, #10B981)', borderRadius: 10, transition: 'width 1s ease-in-out' }} />
      </div>
      <div style={{ fontSize: 11, color: subTextColor }}>
        {isMax ? '🏆 Legendary status — your garden is golden!' : `${nextStage.min - coins} DC to "${nextStage.name}" ${nextStage.petals > stage.petals ? '🌸' : '🌿'}`}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THEME PICKER MODAL
// ═══════════════════════════════════════════════════════════════════
function ThemePickerModal({ currentTheme, unlockedThemes, onThemeChange, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 20, padding: '24px 20px', width: '100%', maxWidth: 340, boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 800, fontSize: 16, color: '#1E293B', marginBottom: 4 }}>🌸 Garden Theme</div>
        <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>Choose your active garden theme</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Default */}
          <button onClick={() => { onThemeChange('default'); onClose(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 12, background: THEMES.default.bg, border: `2px solid ${currentTheme === 'default' ? '#1BA2F6' : THEMES.default.border}`, borderRadius: 12, padding: '10px 14px', cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontSize: 24 }}>☀️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>Sky Garden</div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>Default theme</div>
            </div>
            {currentTheme === 'default' && <span style={{ fontSize: 16, color: '#1BA2F6' }}>✓</span>}
          </button>
          {/* Unlockable themes */}
          {THEME_VOUCHERS.map(tv => {
            const isUnlocked = unlockedThemes.includes(tv.themeKey);
            const theme = THEMES[tv.themeKey];
            const isActive = currentTheme === tv.themeKey;
            return (
              <button key={tv.id} onClick={() => { if (isUnlocked) { onThemeChange(tv.themeKey); onClose(); } }}
                disabled={!isUnlocked}
                style={{ display: 'flex', alignItems: 'center', gap: 12, background: isUnlocked ? theme.bg : '#F8FAFC', border: `2px solid ${isActive ? '#1BA2F6' : isUnlocked ? theme.border : '#E2E8F0'}`, borderRadius: 12, padding: '10px 14px', cursor: isUnlocked ? 'pointer' : 'not-allowed', textAlign: 'left', opacity: isUnlocked ? 1 : 0.6 }}>
                <span style={{ fontSize: 24 }}>{tv.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>{tv.name}</div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>{isUnlocked ? '✓ Unlocked' : `Unlock in Vouchers for ${tv.cost} DC`}</div>
                </div>
                {isActive && <span style={{ fontSize: 16, color: '#1BA2F6' }}>✓</span>}
              </button>
            );
          })}
        </div>
        <button onClick={onClose} style={{ marginTop: 16, width: '100%', background: '#F1F5F9', color: '#64748B', border: 'none', borderRadius: 10, padding: '10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// QR CODE PAGE
// ═══════════════════════════════════════════════════════════════════
function QRCodePage({ onHome, coins }) {
  const [mode, setMode] = useState('scan');
  const [amount, setAmount] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  const [receiveAmount, setReceiveAmount] = useState('');
  const [receiveNote, setReceiveNote] = useState('');
  const [copied, setCopied] = useState(false);

  function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedImage(URL.createObjectURL(file));
  }

  function handleMockPay() {
    if (!amount || Number(amount) <= 0) return;
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setSuccess(true);
    }, 1800);
  }

  function handleCopyAddress() {
    navigator.clipboard.writeText(MOCK_DASH_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onHome} coins={coins} />
        <div style={{ padding: '0 16px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1E3A5F', marginBottom: 6 }}>
            Payments
          </h2>
          <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 18 }}>
            Pay quickly with Dash or generate a professional receive QR for others to send money.
          </p>

          {/* Segmented control */}
          <div
            style={{
              background: '#EAF4FF',
              border: '1px solid #BFDBFE',
              borderRadius: 14,
              padding: 4,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 4,
              marginBottom: 18,
            }}
          >
            <button
              onClick={() => setMode('scan')}
              style={{
                border: 'none',
                borderRadius: 10,
                padding: '10px 12px',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                background: mode === 'scan' ? 'linear-gradient(135deg, #1BA2F6, #0C44AC)' : 'transparent',
                color: mode === 'scan' ? 'white' : '#2563EB',
                transition: 'all 0.2s ease',
              }}
            >
              Scan to Pay
            </button>
            <button
              onClick={() => setMode('receive')}
              style={{
                border: 'none',
                borderRadius: 10,
                padding: '10px 12px',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                background: mode === 'receive' ? 'linear-gradient(135deg, #1BA2F6, #0C44AC)' : 'transparent',
                color: mode === 'receive' ? 'white' : '#2563EB',
                transition: 'all 0.2s ease',
              }}
            >
              Receive
            </button>
          </div>

          {mode === 'scan' && (
            <>
              <div
                style={{
                  background: 'white',
                  border: '1px solid #BFDBFE',
                  borderRadius: 20,
                  padding: '18px 18px 20px',
                  marginBottom: 16,
                  boxShadow: '0 4px 18px rgba(27,162,246,0.08)',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 15, color: '#1E293B', marginBottom: 4 }}>
                  Scan to Pay
                </div>
                <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>
                  Upload a QR image to simulate merchant payment, just like your split-bill image upload flow.
                </div>

                <label
                  style={{
                    display: 'block',
                    border: '1.5px dashed #93C5FD',
                    borderRadius: 16,
                    padding: uploadedImage ? 10 : '24px 16px',
                    textAlign: 'center',
                    background: '#F8FBFF',
                    cursor: 'pointer',
                    marginBottom: 14,
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    style={{ display: 'none' }}
                  />

                  {!uploadedImage ? (
                    <>
                      <div style={{ fontSize: 34, marginBottom: 8 }}>📷</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#2563EB', marginBottom: 4 }}>
                        Upload QR image
                      </div>
                      <div style={{ fontSize: 12, color: '#94A3B8' }}>
                        JPG, PNG, or receipt-style QR mock
                      </div>
                    </>
                  ) : (
                    <img
                      src={uploadedImage}
                      alt="Uploaded QR"
                      style={{
                        width: '100%',
                        maxHeight: 220,
                        objectFit: 'cover',
                        borderRadius: 12,
                      }}
                    />
                  )}
                </label>

                <div style={{ position: 'relative', marginBottom: 14 }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 13,
                      color: '#94A3B8',
                      fontWeight: 700,
                    }}
                  >
                    RM
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '12px 12px 12px 40px',
                      borderRadius: 12,
                      border: '1px solid #BFDBFE',
                      fontSize: 18,
                      fontWeight: 800,
                      outline: 'none',
                    }}
                  />
                </div>

                {!success ? (
                  <button
                    onClick={handleMockPay}
                    disabled={!amount || processing}
                    style={{
                      width: '100%',
                      border: 'none',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: !amount || processing ? 'not-allowed' : 'pointer',
                      background: amount ? 'linear-gradient(135deg, #1BA2F6, #0C44AC)' : '#CBD5E1',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    {processing ? (
                      <>
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            border: '2px solid white',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 0.7s linear infinite',
                          }}
                        />
                        Processing Payment...
                      </>
                    ) : (
                      'Pay with Dash'
                    )}
                  </button>
                ) : (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)',
                      border: '1px solid #A7F3D0',
                      borderRadius: 14,
                      padding: '16px 14px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 34, marginBottom: 6 }}>✅</div>
                    <div style={{ fontWeight: 800, fontSize: 17, color: '#059669' }}>
                      Payment Successful
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                      RM {Number(amount).toFixed(2)} paid via Dash
                    </div>
                    <div style={{ fontSize: 12, color: '#1BA2F6', fontWeight: 700, marginTop: 6 }}>
                      +2 🪙 DC earned
                    </div>
                    <button
                      onClick={() => {
                        setSuccess(false);
                        setAmount('');
                        setUploadedImage(null);
                      }}
                      style={{
                        marginTop: 12,
                        border: 'none',
                        borderRadius: 10,
                        padding: '8px 16px',
                        fontWeight: 700,
                        fontSize: 12,
                        background: '#E2E8F0',
                        color: '#475569',
                        cursor: 'pointer',
                      }}
                    >
                      New Payment
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {mode === 'receive' && (
            <>
              <div
                style={{
                  background: 'white',
                  border: '1px solid #BFDBFE',
                  borderRadius: 20,
                  padding: '20px 18px',
                  marginBottom: 16,
                  boxShadow: '0 4px 18px rgba(27,162,246,0.08)',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 15, color: '#1E293B', marginBottom: 4 }}>
                  Receive Money
                </div>
                <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>
                  Show your QR code so friends can send money instantly.
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 12,
                        color: '#94A3B8',
                        fontWeight: 700,
                      }}
                    >
                      RM
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={receiveAmount}
                      onChange={(e) => setReceiveAmount(e.target.value)}
                      placeholder="Amount"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 10px 10px 38px',
                        borderRadius: 10,
                        border: '1px solid #BFDBFE',
                        fontSize: 13,
                        outline: 'none',
                      }}
                    />
                  </div>

                  <input
                    type="text"
                    value={receiveNote}
                    onChange={(e) => setReceiveNote(e.target.value)}
                    placeholder="Note"
                    style={{
                      flex: 1,
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid #BFDBFE',
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                </div>

                <div
                  style={{
                    border: '1px solid #BFDBFE',
                    borderRadius: 18,
                    padding: '20px 16px',
                    textAlign: 'center',
                    background: 'linear-gradient(180deg, #F8FBFF, #FFFFFF)',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
                    Dash Receive QR
                  </div>

                  <div
                    style={{
                      display: 'inline-block',
                      background: 'white',
                      padding: 12,
                      borderRadius: 14,
                      border: '1px solid #DBEAFE',
                      boxShadow: '0 6px 24px rgba(27,162,246,0.08)',
                      marginBottom: 12,
                    }}
                  >
                    <svg width="170" height="170" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">
                      <rect width="160" height="160" rx="12" fill="white"/>
                      <rect x="10" y="10" width="40" height="40" rx="4" fill="#0C44AC"/>
                      <rect x="16" y="16" width="28" height="28" rx="2" fill="white"/>
                      <rect x="20" y="20" width="20" height="20" rx="1" fill="#0C44AC"/>
                      <rect x="110" y="10" width="40" height="40" rx="4" fill="#0C44AC"/>
                      <rect x="116" y="16" width="28" height="28" rx="2" fill="white"/>
                      <rect x="120" y="20" width="20" height="20" rx="1" fill="#0C44AC"/>
                      <rect x="10" y="110" width="40" height="40" rx="4" fill="#0C44AC"/>
                      <rect x="16" y="116" width="28" height="28" rx="2" fill="white"/>
                      <rect x="20" y="120" width="20" height="20" rx="1" fill="#0C44AC"/>

                      {[60,65,70,75,80,85,90,95,100,105].map(x =>
                        [14,19,24,29,34,39].map(y => (
                          (Math.sin(x * y) > 0.15) && <rect key={`${x}-${y}`} x={x} y={y} width="4" height="4" fill="#0C44AC"/>
                        ))
                      )}

                      {[12,17,22,27,32,37,42,47].map(x =>
                        [60,65,70,75,80,85,90,95,100,105,110,115].map(y => (
                          (Math.cos(x + y) > 0.1) && <rect key={`${x}-${y}`} x={x} y={y} width="4" height="4" fill="#0C44AC"/>
                        ))
                      )}

                      {[60,65,70,75,80,85,90,95,100,105,110,115,120,125,130,135,140].map(x =>
                        [60,65,70,75,80,85,90,95,100,105,110,115,120,125,130,135,140].map(y => (
                          (Math.sin(x * 0.3) * Math.cos(y * 0.3) > 0.12) && <rect key={`${x}-${y}`} x={x} y={y} width="4" height="4" fill="#1BA2F6"/>
                        ))
                      )}

                      <rect x="62" y="62" width="36" height="36" rx="8" fill="white"/>
                      <rect x="64" y="64" width="32" height="32" rx="7" fill="#1BA2F6"/>
                      <text x="80" y="84" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">D</text>
                    </svg>
                  </div>

                  <div style={{ fontWeight: 800, fontSize: 18, color: '#1E293B', marginBottom: 4 }}>
                    {receiveAmount ? `RM ${Number(receiveAmount).toFixed(2)}` : 'Any Amount'}
                  </div>

                  <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
                    {receiveNote || 'Dash payment request'}
                  </div>

                  <div style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'monospace', marginBottom: 10 }}>
                    {MOCK_DASH_ADDRESS.slice(0, 18)}...{MOCK_DASH_ADDRESS.slice(-8)}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleCopyAddress}
                      style={{
                        flex: 1,
                        border: '1px solid #BFDBFE',
                        borderRadius: 10,
                        padding: '10px 12px',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                        background: '#EFF6FF',
                        color: '#2563EB',
                      }}
                    >
                      {copied ? 'Copied' : 'Copy Address'}
                    </button>

                    <button
                      style={{
                        flex: 1,
                        border: 'none',
                        borderRadius: 10,
                        padding: '10px 12px',
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #1BA2F6, #0C44AC)',
                        color: 'white',
                      }}
                    >
                      Share QR
                    </button>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DASH WALLET PAGE
// ═══════════════════════════════════════════════════════════════════
function WalletPage({ onHome, coins }) {
  const [walletBalance, setWalletBalance] = useState(() => loadWallet());
  const [topupAmt, setTopupAmt] = useState('');
  const [topupStatus, setTopupStatus] = useState('idle');
  const [transactions] = useState([
    { type: 'in',  label: 'Bought Dash',        amount: 50.00, date: '10 Apr 2026', icon: '⬇️' },
    { type: 'out', label: 'Split Bill — Nando\'s', amount: 18.75, date: '9 Apr 2026',  icon: '⬆️' },
    { type: 'in',  label: 'Bought Dash',        amount: 20.00, date: '5 Apr 2026',  icon: '⬇️' },
    { type: 'out', label: 'Split Bill — Sushi King', amount: 24.00, date: '2 Apr 2026',  icon: '⬆️' },
  ]);

  const DASH_PRICE_MYR = 18.42;

  function handleTopup() {
    if (!topupAmt || isNaN(topupAmt) || Number(topupAmt) <= 0) return;
    setTopupStatus('processing');
    setTimeout(() => {
      const newBal = walletBalance + Number(topupAmt);
      setWalletBalance(newBal);
      saveWallet(newBal);
      setTopupStatus('done');
      setTopupAmt('');
      setTimeout(() => setTopupStatus('idle'), 3000);
    }, 1800);
  }

  const dashAmount = (walletBalance / DASH_PRICE_MYR).toFixed(4);

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onHome} coins={coins} />
        <div style={{ padding: '0 16px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E3A5F', marginBottom: 4 }}>💎 Dash Wallet</h2>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 20 }}>Earn interest at 3.43% per annum — grow your savings effortlessly.</p>

          <div style={{ background: 'linear-gradient(135deg, #0C44AC, #1BA2F6)', borderRadius: 20, padding: '24px 22px', color: 'white', marginBottom: 20, position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(12,68,172,0.35)' }}>
            <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
            <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <div style={{ fontSize: 11, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Wallet Balance · 3.43% p.a.</div>
            <div style={{ fontSize: 34, fontWeight: 900, fontFamily: "'Syne', sans-serif", marginBottom: 2 }}>RM {walletBalance.toFixed(2)}</div>
            <div style={{ fontSize: 14, opacity: 0.75, marginBottom: 16 }}>≈ {dashAmount} DASH</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600 }}>
                💹 1 DASH = RM {DASH_PRICE_MYR}
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600 }}>
                🪙 {coins} DC balance
              </div>
            </div>
          </div>

          <div style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 16, padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1E293B', marginBottom: 4 }}>🏦 Buy Dash (Top Up)</div>
            <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 12 }}>Convert RM to Dash — stored securely in your wallet.</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {['20', '50', '100', '200'].map(v => (
                <button key={v} onClick={() => setTopupAmt(v)}
                  style={{ flex: 1, background: topupAmt === v ? '#1BA2F6' : '#EFF6FF', color: topupAmt === v ? 'white' : '#1BA2F6', border: '1px solid #BFDBFE', borderRadius: 8, padding: '7px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  RM{v}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>RM</span>
                <input type="number" value={topupAmt} onChange={e => setTopupAmt(e.target.value)} placeholder="Custom amount"
                  style={{ width: '100%', paddingLeft: 34, paddingRight: 10, paddingTop: 9, paddingBottom: 9, borderRadius: 8, border: '1px solid #BFDBFE', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>
              <button onClick={handleTopup} disabled={!topupAmt || topupStatus === 'processing'}
                style={{ background: topupAmt ? 'linear-gradient(135deg, #1BA2F6, #0C44AC)' : '#CBD5E1', color: 'white', border: 'none', borderRadius: 8, padding: '0 18px', fontWeight: 700, fontSize: 13, cursor: topupAmt ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                {topupStatus === 'processing' ? (
                  <><span style={{ width: 12, height: 12, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Buying…</>
                ) : topupStatus === 'done' ? '✓ Done!' : 'Buy'}
              </button>
            </div>
            {topupAmt && !isNaN(topupAmt) && Number(topupAmt) > 0 && (
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
                ≈ {(Number(topupAmt) / DASH_PRICE_MYR).toFixed(4)} DASH at current rate
              </div>
            )}
          </div>

          <div style={{ background: 'linear-gradient(135deg, #FFF7ED, #FEF3C7)', border: '1px solid #FDE68A', borderRadius: 14, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 8 }}>💡 Why keep Dash in your wallet?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                ['⚡', 'Instant payments'],
                ['💹', 'Dash can appreciate in value over time'],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#78350F', alignItems: 'flex-start' }}>
                  <span>{icon}</span><span>{text}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ fontWeight: 700, fontSize: 14, color: '#1E3A5F', marginBottom: 10 }}>Recent Transactions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {transactions.map((tx, i) => (
              <div key={i} style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 12, padding: '11px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>{tx.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>{tx.label}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>{tx.date}</div>
                  </div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, color: tx.type === 'in' ? '#10B981' : '#EF4444' }}>
                  {tx.type === 'in' ? '+' : '-'}RM {tx.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// LEVEL-UP TOAST
// ═══════════════════════════════════════════════════════════════════
function LevelUpToast({ stage, onDismiss }) {
  React.useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  const isPetal = stage.petals > 0;
  return (
    <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: stage.name === 'Golden Orchid' ? 'linear-gradient(135deg, #F9A825, #FF8F00)' : 'linear-gradient(135deg, #10B981, #059669)', color: 'white', borderRadius: 14, padding: '14px 22px', fontWeight: 700, fontSize: 14, zIndex: 1000, boxShadow: '0 6px 24px rgba(16,185,129,0.45)', display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap', animation: 'slideDown 0.4s ease' }}>
      <span style={{ fontSize: 22 }}>{isPetal ? '🌸' : '🌿'}</span>
      <div>
        <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 500 }}>Garden evolved!</div>
        <div>{stage.name}{isPetal ? ` — petal ${stage.petals} bloomed!` : ''}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════
function CoinBadge({ coins = 0, size = 'sm' }) {
  const big = size === 'lg';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: big ? 6 : 4, background: 'linear-gradient(135deg, #1BA2F6 0%, #0C44AC 100%)', color: 'white', borderRadius: 999, padding: big ? '6px 14px' : '3px 10px', fontWeight: 700, fontSize: big ? 16 : 12, boxShadow: '0 2px 8px rgba(27,162,246,0.35)', letterSpacing: 0.3 }}>
      <span style={{ fontSize: big ? 18 : 14, lineHeight: 1 }}>🪙</span>
      {coins} DC
    </span>
  );
}

function Logo({ sub = true }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: sub ? 6 : 0 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
        <img src={dashLogo} alt="Dash" style={{ height: 42, objectFit: 'contain' }} />
        <span style={{ fontSize: 32, fontWeight: 900, letterSpacing: -1, fontFamily: "'Syne', sans-serif", color: '#1BA2F6' }}>Split</span>
      </div>
      {sub && <p style={{ fontSize: 12, color: '#94A3B8', margin: '4px 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 500 }}>Split fast · Pay with Dash · Grow your garden</p>}
    </div>
  );
}

function Steps({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
      {[1, 2, 3].map((s, i) => (
        <React.Fragment key={s}>
          <div style={{ background: s < current ? '#3B82F6' : s === current ? '#1D4ED8' : '#E5E7EB', color: s <= current ? 'white' : '#9CA3AF', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, transition: 'all 0.3s' }}>
            {s < current ? '✓' : s}
          </div>
          {i < 2 && <div style={{ width: 40, height: 2, background: s < current ? '#3B82F6' : '#D1D5DB', transition: 'background 0.3s' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function ParticipantRow({ index, participant, onChange, onRemove, showRemove }) {
  const { name, method, bankName, bankAccount } = participant;
  return (
    <div style={{ background: '#F0F7FF', borderRadius: 10, padding: '12px 14px', marginBottom: 10, border: '1px solid #BFDBFE' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <input type="text" value={name} onChange={e => onChange(index, { name: e.target.value })} placeholder="Name"
          style={{ flex: 1, padding: '7px 10px', borderRadius: 7, fontSize: 13, border: '1px solid #93C5FD', outline: 'none', fontFamily: 'inherit' }} />
        <select value={method} onChange={e => onChange(index, { method: e.target.value })}
          style={{ padding: '7px 8px', borderRadius: 7, fontSize: 12, border: '1px solid #93C5FD', outline: 'none', background: 'white', fontFamily: 'inherit', cursor: 'pointer' }}>
          <option value="dash">⚡ Dash</option>
          <option value="usdc">🔵 USDC</option>
          <option value="usdt">🟢 USDT</option>
          <option value="bank">🏦 Bank</option>
          <option value="cash">💵 Cash</option>
        </select>
        {showRemove && (
          <button onClick={() => onRemove(index)} style={{ background: 'none', border: 'none', color: '#E24B4A', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>×</button>
        )}
      </div>
      {method === 'dash' && <div style={{ fontSize: 12, color: '#1BA2F6', paddingLeft: 2, fontWeight: 600 }}>⚡ Earns +2 🪙 DC · grows your flower faster!</div>}
      {method === 'usdc' && <div style={{ fontSize: 12, color: '#2775CA', paddingLeft: 2, fontWeight: 600 }}>🔵 Auto-swapped to Dash via Maya · Organiser receives Dash</div>}
      {method === 'usdt' && <div style={{ fontSize: 12, color: '#26A17B', paddingLeft: 2, fontWeight: 600 }}>🟢 Auto-swapped to Dash via Maya · Organiser receives Dash</div>}
      {method === 'bank' && (
        <>
          <div style={{ fontSize: 12, color: '#9CA3AF', paddingLeft: 2, marginBottom: 6 }}>🏦 Earns +1 🪙 DC</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={bankName} onChange={e => onChange(index, { bankName: e.target.value })} placeholder="Bank (e.g. Maybank)"
              style={{ flex: 1, padding: '7px 10px', borderRadius: 7, fontSize: 12, border: '1px solid #93C5FD', outline: 'none', fontFamily: 'inherit' }} />
            <input type="text" value={bankAccount} onChange={e => onChange(index, { bankAccount: e.target.value })} placeholder="Account No."
              style={{ flex: 1, padding: '7px 10px', borderRadius: 7, fontSize: 12, border: '1px solid #93C5FD', outline: 'none', fontFamily: 'inherit' }} />
          </div>
        </>
      )}
      {method === 'cash' && <div style={{ fontSize: 12, color: '#9CA3AF', paddingLeft: 2 }}>💵 Earns +1 🪙 DC</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// USDC/USDT SWAP PAYMENT PANEL
// ═══════════════════════════════════════════════════════════════════
const MOCK_USDC_ADDRESS = 'TJCo3sPbPVF9qvxGbhRGHqhV4nGpwqDhkL';
const MOCK_USDT_ADDRESS = 'TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE';

function StablecoinPayPanel({ participant, amount, currency, onPaid }) {
  const token = participant.method === 'usdc' ? 'USDC' : 'USDT';
  const tokenColor = token === 'USDC' ? '#2775CA' : '#26A17B';
  const tokenBg   = token === 'USDC' ? '#EFF6FF' : '#F0FDF4';
  const tokenBorder = token === 'USDC' ? '#BFDBFE' : '#BBF7D0';
  const mockAddr = token === 'USDC' ? MOCK_USDC_ADDRESS : MOCK_USDT_ADDRESS;

  const [quote, setQuote]         = useState(null);   // { stableAmount, dashAmount, fee, rate }
  const [quoteLoading, setQL]     = useState(false);
  const [quoteErr, setQE]         = useState(null);
  const [copied, setCopied]       = useState(false);
  const [swapStatus, setSwapStatus] = useState('idle'); // idle | confirming | swapping | done | error
  const [swapTx, setSwapTx]       = useState(null);

  // fetch quote on mount
  React.useEffect(() => {
    let alive = true;
    setQL(true);
    axios.post(`${BACKEND}/get-quote`, { amountMYR: amount, token })
      .then(r => { if (alive) setQuote(r.data); })
      .catch(() => { if (alive) setQE('Could not fetch live price. Using estimate.'); })
      .finally(() => { if (alive) setQL(false); });
    return () => { alive = false; };
  }, [amount, token]);

  function handleCopy() {
    navigator.clipboard.writeText(mockAddr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function handleConfirmSent() {
    setSwapStatus('swapping');
    try {
      const res = await axios.post(`${BACKEND}/execute-swap`, {
        token,
        stableAmount: quote?.stableAmount ?? (amount / 4.7),
        recipientDashAddress: MOCK_DASH_ADDRESS,
        participantName: participant.name,
      });
      setSwapTx(res.data.txId || 'mock-tx-' + Date.now());
      setSwapStatus('done');
      onPaid?.();
    } catch {
      setSwapStatus('error');
      setTimeout(() => setSwapStatus('idle'), 3000);
    }
  }

  if (swapStatus === 'done') {
    return (
      <div style={{ background: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)', border: '1px solid #A7F3D0', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#059669', marginBottom: 4 }}>✅ Swap complete — Dash sent!</div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          {token} → Dash swap executed via Maya Protocol.<br />
          Organiser received Dash via InstantSend.
        </div>
        {swapTx && <div style={{ fontSize: 10, color: '#94A3B8', fontFamily: 'monospace', marginTop: 6 }}>tx: {swapTx}</div>}
      </div>
    );
  }

  return (
    <div style={{ background: tokenBg, border: `1px solid ${tokenBorder}`, borderRadius: 10, padding: '12px 14px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>{token === 'USDC' ? '🔵' : '🟢'}</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: tokenColor }}>Pay with {token}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94A3B8', background: 'white', border: `1px solid ${tokenBorder}`, borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>Auto-swaps to Dash</span>
      </div>

      {/* Quote row */}
      <div style={{ background: 'white', border: `1px solid ${tokenBorder}`, borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
        {quoteLoading && (
          <div style={{ fontSize: 12, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, border: '2px solid #BFDBFE', borderTopColor: tokenColor, borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
            Fetching live price…
          </div>
        )}
        {quoteErr && !quoteLoading && (
          <div style={{ fontSize: 12, color: '#F59E0B' }}>⚠️ {quoteErr}</div>
        )}
        {quote && !quoteLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: '#6B7280' }}>You pay</span>
              <span style={{ fontWeight: 800, color: tokenColor, fontSize: 15 }}>{quote.stableAmount.toFixed(4)} {token}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94A3B8' }}>
              <span>Organiser gets</span>
              <span style={{ fontWeight: 600, color: '#1BA2F6' }}>≈ {quote.dashAmount.toFixed(4)} DASH</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#CBD5E1', borderTop: `1px solid ${tokenBorder}`, paddingTop: 4, marginTop: 2 }}>
              <span>App fee (0.75%)</span>
              <span>{quote.fee.toFixed(4)} {token}</span>
            </div>
            <div style={{ fontSize: 10, color: '#CBD5E1' }}>Rate: 1 DASH = {quote.rate} {token} · via CoinGecko</div>
          </div>
        )}
      </div>

      {/* Payment address */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4, fontWeight: 600 }}>Send {token} to this address:</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', border: `1px solid ${tokenBorder}`, borderRadius: 8, padding: '7px 10px' }}>
          <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#374151', flex: 1, wordBreak: 'break-all' }}>{mockAddr}</span>
          <button onClick={handleCopy} style={{ background: tokenColor, color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {copied ? '✓' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Confirm button */}
      {swapStatus === 'error' && (
        <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 6 }}>⚠️ Swap failed. Please try again.</div>
      )}
      <button
        onClick={handleConfirmSent}
        disabled={swapStatus === 'swapping' || quoteLoading}
        style={{ width: '100%', background: swapStatus === 'swapping' ? '#9CA3AF' : `linear-gradient(135deg, ${tokenColor}, ${tokenColor}cc)`, color: 'white', border: 'none', borderRadius: 8, padding: '9px 12px', fontWeight: 700, fontSize: 12, cursor: swapStatus === 'swapping' ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
      >
        {swapStatus === 'swapping'
          ? <><span style={{ width: 10, height: 10, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Executing swap…</>
          : `✓ I've sent the ${token} — execute swap`}
      </button>
      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6, textAlign: 'center' }}>
        Maya Protocol swaps {token} → Dash · Organiser receives Dash via InstantSend
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SEND LINK BUTTON — generates WhatsApp pay link per participant
// ═══════════════════════════════════════════════════════════════════
function SendLinkButton({ participant, amount, merchant = 'Restaurant' }) {
  const [copied, setCopied] = useState(false);

  function buildPayUrl() {
    const base = window.location.origin + window.location.pathname;
    const p = new URLSearchParams({
      name:    participant.name,
      amount:  amount.toFixed(2),
      method:  participant.method,
      merchant,
      ...(participant.bankName    ? { bankName:    participant.bankName    } : {}),
      ...(participant.bankAccount ? { bankAccount: participant.bankAccount } : {}),
    });
    return `${base}?paylink=1&${p.toString()}`;
  }

  function handleWhatsApp() {
    const url  = buildPayUrl();
    const text = `Hey ${participant.name}! Your share for ${merchant} is RM${amount.toFixed(2)} 🧾\nPay here 👉 ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  }

  function handleCopy() {
    navigator.clipboard.writeText(buildPayUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ display: 'inline-flex', gap: 5 }}>
      <button
        onClick={handleWhatsApp}
        style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: 7, padding: '6px 11px', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        Send via WhatsApp
      </button>
      <button
        onClick={handleCopy}
        style={{ background: copied ? '#10B981' : '#F1F5F9', color: copied ? 'white' : '#475569', border: '1px solid #E2E8F0', borderRadius: 7, padding: '6px 10px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
      >
        {copied ? '✓ Copied' : '🔗'}
      </button>
    </div>
  );
}


function ActionButton({ participant, amount, currency, onPaid }) {
  const [status, setStatus] = useState('idle');
  const { method } = participant;

  // USDC / USDT — render dedicated panel
  if (method === 'usdc' || method === 'usdt') {
    return <StablecoinPayPanel participant={participant} amount={amount} currency={currency} onPaid={onPaid} />;
  }

  async function handleAction() {
    setStatus('processing');
    try {
      if (method === 'dash') {
        await Promise.race([sendPayment(participant.name, amount), new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))]);
      } else {
        await new Promise(r => setTimeout(r, 800));
      }
      setStatus('done');
      onPaid?.();
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  }

  if (status === 'done') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1D6FB8', fontWeight: 700, fontSize: 13 }}>
        <span style={{ fontSize: 16 }}>✓</span> {method === 'dash' ? 'Sent!' : 'Paid!'}
      </span>
    );
  }

  const label = status === 'processing' ? (method === 'dash' ? 'Sending…' : 'Marking…') : (method === 'dash' ? `Pay ${currency}${amount.toFixed(2)}` : '✓ Mark as Paid');
  const bg = status === 'processing' ? '#9CA3AF' : method === 'dash' ? '#1BA2F6' : '#2563EB';

  return (
    <button onClick={handleAction} disabled={status === 'processing'}
      style={{ background: bg, color: 'white', border: 'none', borderRadius: 7, padding: '6px 12px', fontWeight: 700, fontSize: 12, cursor: status === 'processing' ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'background 0.2s', whiteSpace: 'nowrap' }}>
      {status === 'processing' && <span style={{ width: 10, height: 10, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />}
      {label}
    </button>
  );
}

function TopBar({ onBack, label = '← Home', coins = 0, onHistory = null }) {
  const stage = getStage(coins);
  return (
    <div style={{ width: '100%', maxWidth: 480, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', marginBottom: 20 }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onHistory && (
          <button onClick={onHistory}
            style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#2563EB', cursor: 'pointer' }}>
            Split History
          </button>
        )}
        {!onHistory && <span style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>🌸 {stage.name}</span>}
        <CoinBadge coins={coins} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HOME PAGE — theme-aware hero + screenshot-matching card grid
// ═══════════════════════════════════════════════════════════════════
function HomePage({ onSplitBill, onVouchers, onHistory, onQR, onWallet, currentTheme, coins = 0, unlockedThemes, onThemeChange }) {
  const stage = getStage(coins);
  const si = getStageIndex(coins);
  const nextStage = GARDEN_STAGES[Math.min(si + 1, GARDEN_STAGES.length - 1)];
  const isMax = si >= GARDEN_STAGES.length - 1;
  const progress = isMax ? 100 : Math.round(((coins - stage.min) / (nextStage.min - stage.min)) * 100);
  const isGolden = stage.name === 'Golden Orchid';
  const theme = THEMES[currentTheme] || THEMES.default;
  const [showThemePicker, setShowThemePicker] = useState(false);

  // Determine text colors based on dark theme
  const isDark = theme.dark;

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#0F172A' : '#F0F9FF', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 32 }}>
      {showThemePicker && (
        <ThemePickerModal
          currentTheme={currentTheme}
          unlockedThemes={unlockedThemes}
          onThemeChange={onThemeChange}
          onClose={() => setShowThemePicker(false)}
        />
      )}

      {/* ── Hero: full-width, theme-driven background ── */}
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: '16px 16px 0', boxSizing: 'border-box' }}>
        <div style={{
          background: theme.heroBg,
          borderRadius: 24,
          padding: '28px 22px 0 22px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: theme.heroShadow,
        }}>
          {/* Top row: logo + theme button + coin balance */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <img src={dashLogo} alt="Dash" style={{ height: 32, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              <span style={{ fontSize: 24, fontWeight: 900, letterSpacing: -1, fontFamily: "'Syne', sans-serif", color: 'white' }}>Split</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setShowThemePicker(true)}
                style={{ background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 8, padding: '6px 12px', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {theme.icon} Theme
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '5px 12px' }}>
                <span style={{ fontSize: 13, lineHeight: 1 }}>🪙</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'white' }}>{coins} DC</span>
              </div>
            </div>
          </div>

          {/* Flower + garden info row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
            <div style={{ flexShrink: 0, marginBottom: -4 }}>
              <FlowerSVG coins={coins} size={130} />
            </div>
            <div style={{ flex: 1, paddingBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>🌸 Financial Garden</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'white', fontFamily: "'Syne', sans-serif", marginBottom: 2 }}>{stage.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 10, lineHeight: 1.4 }}>{stage.desc}</div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 10, overflow: 'hidden', marginBottom: 4 }}>
                <div style={{ width: `${progress}%`, height: '100%', background: isGolden ? 'linear-gradient(90deg, #FFD700, #FF8F00)' : 'rgba(255,255,255,0.9)', borderRadius: 10, transition: 'width 1s ease-in-out' }} />
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>
                {isMax ? '🏆 Legendary!' : `${nextStage.min - coins} DC → "${nextStage.name}"`}
              </div>
            </div>
          </div>

          {/* Welcome strip */}
          <div style={{ background: 'rgba(0,0,0,0.15)', margin: '0 -22px', padding: '10px 22px', marginTop: 8 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
              👋 Welcome back &nbsp;·&nbsp; Pay with <strong>⚡ Dash</strong> → earn <strong>+2 🪙 DC</strong> · Redeem for vouchers!
            </div>
          </div>
        </div>
      </div>
      {/* ── Action cards ── */}
      <div style={{ width: '100%', maxWidth: 480, padding: '16px 16px 0', boxSizing: 'border-box' }}>
        {/* Bank + QR Code are tall (2/3 width), Split Bill + Voucher stacked on right (1/3 width) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.65fr', gridTemplateRows: '1fr 1fr', gap: 12, height: 240 }}>

          {/* Wallet — tall, left col, spans both rows */}
          <button
            onClick={onWallet}
            style={{ all: 'unset', cursor: 'pointer', gridRow: '1 / 3', gridColumn: '1 / 2' }}
          >
            <div
              style={{
                background: 'white',
                border: `1px solid ${isDark ? '#334155' : '#BFDBFE'}`,
                borderRadius: 16,
                padding: '20px 14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                boxShadow: '0 2px 12px rgba(27,162,246,0.08)',
                height: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
                transition: 'box-shadow 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(27,162,246,0.18)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(27,162,246,0.08)'}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, overflow: 'hidden', flexShrink: 0 }}>
                <img src={saveIcon} alt="Wallet" style={{ width:'120%', height: '120%', objectFit: 'cover' }} />
              </div>

              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B', marginBottom: 4 }}>
                  Wallet
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8', lineHeight: 1.5 }}>
                  Save & manage funds
                </div>
              </div>
            </div>
          </button>

          {/* Pay — tall, center col, spans both rows */}
          <button
            onClick={onQR}
            style={{ all: 'unset', cursor: 'pointer', gridRow: '1 / 3', gridColumn: '2 / 3' }}
          >
            <div
              style={{
                background: 'white',
                border: `1px solid ${isDark ? '#334155' : '#BFDBFE'}`,
                borderRadius: 16,
                padding: '20px 14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                boxShadow: '0 2px 12px rgba(27,162,246,0.08)',
                height: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
                transition: 'box-shadow 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(27,162,246,0.18)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(27,162,246,0.08)'}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, overflow: 'hidden', flexShrink: 0 }}>
                <img src={scanQRIcon} alt="Pay" style={{ width: '120%', height: '120%', objectFit: 'cover' }} />
              </div>

              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B', marginBottom: 4 }}>
                  Pay
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8', lineHeight: 1.5 }}>
                  Scan or receive money
                </div>
              </div>
            </div>
          </button>

          {/* Split Bill — top of right col */}
          <button onClick={onSplitBill} style={{ all: 'unset', cursor: 'pointer', gridRow: '1 / 2', gridColumn: '3 / 4' }}>
            <div
              style={{
                background: 'white',
                border: `1px solid ${isDark ? '#334155' : '#BFDBFE'}`,
                borderRadius: 16,
                padding: '12px 8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: '0 2px 12px rgba(27,162,246,0.08)',
                height: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
                transition: 'box-shadow 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(27,162,246,0.18)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(27,162,246,0.08)'}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, overflow: 'hidden', flexShrink: 0 }}>
                <img src={splitBillImg} alt="Split Bill" style={{ width: 40, height: 40, objectFit: 'cover' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#1E293B', lineHeight: 1.3 }}>
                Split
                <br />
                Bill
              </div>
            </div>
          </button>

          {/* Voucher — bottom of right col */}
          <button onClick={onVouchers} style={{ all: 'unset', cursor: 'pointer', gridRow: '2 / 3', gridColumn: '3 / 4' }}>
            <div
              style={{
                background: 'white',
                border: `1px solid ${isDark ? '#334155' : '#BFDBFE'}`,
                borderRadius: 16,
                padding: '12px 8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: '0 2px 12px rgba(27,162,246,0.08)',
                height: '100%',
                boxSizing: 'border-box',
                textAlign: 'center',
                transition: 'box-shadow 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(27,162,246,0.18)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(27,162,246,0.08)'}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, overflow: 'hidden', flexShrink: 0 }}>
                <img src={voucherImg} alt="Vouchers" style={{ width: 40, height: 40, objectFit: 'cover' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#1E293B' }}>
                Voucher
              </div>
            </div>
          </button>
        </div>
      </div>

      <p style={{ fontSize: 11, color: '#CBD5E1', marginTop: 28, letterSpacing: 0.5 }}>Powered by Claude AI + Dash Platform</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SPLIT HISTORY
// ═══════════════════════════════════════════════════════════════════
function HistoryPage({ onHome, onDetail, coins }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    axios.get(`${BACKEND}/split-history`)
      .then(res => { if (res.data.success) setHistory(res.data.history); else setError('Could not load history.'); })
      .catch(() => setError('Failed to reach server.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onHome} coins={coins} />
        <div style={{ padding: '0 16px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E3A5F', marginBottom: 4 }}>Split History</h2>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>Your latest splits recorded on Dash Platform. Tap to see details.</p>
          {loading && <div style={{ textAlign: 'center', padding: '32px 0', color: '#94A3B8', fontSize: 13 }}><span style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid #BFDBFE', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 0.7s linear infinite', verticalAlign: 'middle', marginRight: 8 }} />Loading from Dash Platform...</div>}
          {error && !loading && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#DC2626', textAlign: 'center' }}>⚠️ {error}</div>}
          {!loading && !error && history.length === 0 && <div style={{ textAlign: 'center', padding: '32px 0', color: '#94A3B8', fontSize: 13 }}>No splits recorded yet. Complete your first split to see it here!</div>}
          {!loading && history.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {history.map((h, i) => (
                <button key={h.id || i} onClick={() => onDetail(h)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                  <div style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 14, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(27,162,246,0.06)' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1E293B' }}>{h.merchant}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{h.date}{h.people > 0 ? ` · ${h.people} people` : ''}</div>
                      <div style={{ fontSize: 11, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4, color: h.paymentStatus === 'settled' ? '#10B981' : '#F59E0B', fontWeight: 600 }}>
                        <span>{h.paymentStatus === 'settled' ? '✓ Settled' : '⏳ Pending'}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#2563EB' }}>RM {Number(h.totalAmount).toFixed(2)}</div>
                      <span style={{ color: '#BFDBFE', fontSize: 18 }}>›</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <a href={CONTRACT_EXPLORER} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1BA2F6', textDecoration: 'underline' }}>🔗 View all on Dash Explorer</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HISTORY DETAIL PAGE
// ═══════════════════════════════════════════════════════════════════
function HistoryDetailPage({ record, onBack, coins }) {
  const methodLabel = m => m === 'dash' ? '⚡ Dash' : m === 'bank' ? '🏦 Bank' : m === 'usdc' ? '🔵 USDC→Dash' : m === 'usdt' ? '🟢 USDT→Dash' : '💵 Cash';
  const methodColor = m => m === 'dash' ? '#1BA2F6' : m === 'bank' ? '#6366F1' : m === 'usdc' ? '#2775CA' : m === 'usdt' ? '#26A17B' : '#10B981';
  const settled = record.paymentStatus === 'settled';

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onBack} label="← History" coins={coins} />
        <div style={{ padding: '0 16px' }}>
          <div style={{ background: 'linear-gradient(135deg, #1BA2F6, #0C44AC)', borderRadius: 16, padding: '20px 22px', color: 'white', marginBottom: 20, boxShadow: '0 6px 24px rgba(27,162,246,0.3)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Split Record</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Syne', sans-serif", marginBottom: 2 }}>{record.merchant}</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 16 }}>{record.date}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 2 }}>Total Bill</div>
                <div style={{ fontSize: 28, fontWeight: 900 }}>RM {Number(record.totalAmount).toFixed(2)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>Status</div>
                <span style={{ background: settled ? 'rgba(16,185,129,0.25)' : 'rgba(245,158,11,0.25)', border: `1px solid ${settled ? 'rgba(16,185,129,0.5)' : 'rgba(245,158,11,0.5)'}`, borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 700, color: settled ? '#6EE7B7' : '#FDE68A' }}>
                  {settled ? '✓ Settled' : '⏳ Pending'}
                </span>
              </div>
            </div>
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1E3A5F', marginBottom: 10 }}>Participants ({record.people || record.participants?.length || 0})</h3>
          {(!record.participants || record.participants.length === 0) ? (
            <div style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: '16px 0' }}>No participant details available.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {record.participants.map((p, i) => (
                <div key={i} style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 12, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1E293B', marginBottom: 3 }}>{p.name}</div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: methodColor(p.paymentMethod), background: methodColor(p.paymentMethod) + '18', borderRadius: 999, padding: '2px 8px' }}>{methodLabel(p.paymentMethod)}</span>
                    {p.paymentMethod === 'dash' && p.dashAddress && <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 4, fontFamily: 'monospace' }}>{p.dashAddress.slice(0, 14)}…{p.dashAddress.slice(-6)}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: '#2563EB' }}>RM {Number(p.amount).toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: '#1BA2F6', fontWeight: 600, marginTop: 2 }}>+{p.paymentMethod === 'dash' ? 2 : 1} 🪙 DC</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 12, color: '#1E40AF' }}>📋 Document ID</span>
            <a href={`https://testnet.platform-explorer.com/document/${record.id}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#1BA2F6', textDecoration: 'underline', fontFamily: 'monospace' }}>
              {record.id ? `${record.id.slice(0, 10)}…` : '—'}
            </a>
          </div>
          <button onClick={onBack} className="w-full py-2 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300 transition text-sm">← Back to History</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 1 — Split Bill
// ═══════════════════════════════════════════════════════════════════
function Page1({ onNext, onHome, onHistory, coins = 0 }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState([
    { name: '', method: 'dash', bankName: '', bankAccount: '' },
    { name: '', method: 'bank', bankName: '', bankAccount: '' },
  ]);

  const validParticipants = participants.filter(p => p.name.trim().length > 0);
  const canGo = file && validParticipants.length >= 2;

  function updateParticipant(index, fields) { setParticipants(prev => prev.map((p, i) => i === index ? { ...p, ...fields } : p)); }
  function addParticipant() { if (participants.length >= MAX_PARTICIPANTS) return; setParticipants(prev => [...prev, { name: '', method: 'cash', bankName: '', bankAccount: '' }]); }
  function removeParticipant(index) { if (participants.length <= 2) return; setParticipants(prev => prev.filter((_, i) => i !== index)); }

  async function handleGo() {
    if (!canGo) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('receipt', file);
    try {
      const result = await axios.post(`${BACKEND}/parse-bill`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const billItems = result.data.bill.items.map(item => ({ item: item.name, price: item.price, selected: [] }));
      onNext({ items: billItems, participants: validParticipants });
    } catch (err) {
      console.error(err);
      alert('Failed to process the document. Please try again.');
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onHome} coins={coins} onHistory={onHistory} />
      </div>
      <div style={{ marginBottom: 8 }}><Steps current={1} /></div>
      <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-xl" style={{ border: '1px solid #BFDBFE' }}>
        <h2 className="text-lg font-semibold mb-2 text-gray-700">Upload Bill Image</h2>
        <p className="text-xs text-gray-400 mb-3">Claude AI reads your receipt automatically</p>
        <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} className="block w-full mb-5 border p-2 rounded text-sm" />
        <h2 className="text-lg font-semibold mb-1 text-gray-700">Add Participants</h2>
        <p className="text-xs text-gray-400 mb-3">
          Pay with <span style={{ color: '#1BA2F6', fontWeight: 700 }}>⚡ Dash</span> → earn <CoinBadge coins={2} /> &nbsp;|&nbsp; Other methods → <CoinBadge coins={1} />
        </p>
        {participants.map((p, index) => (
          <ParticipantRow key={index} index={index} participant={p} onChange={updateParticipant} onRemove={removeParticipant} showRemove={participants.length > 2} />
        ))}
        {participants.length < MAX_PARTICIPANTS && (
          <button onClick={addParticipant} className="text-sm text-blue-600 underline mb-4 mt-1">+ Add person</button>
        )}
        <button onClick={handleGo} disabled={loading || !canGo}
          className={`w-full py-2 mt-2 ${canGo ? 'bg-blue-600 hover:bg-blue-800' : 'bg-gray-400'} text-white font-semibold rounded-md transition`}>
          {loading ? 'Reading receipt…' : 'Go →'}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 2
// ═══════════════════════════════════════════════════════════════════
function Page2({ items: initialItems, participants, onNext, onBack }) {
  const [items, setItems] = useState(initialItems);
  const [currency] = useState('RM');
  const selectedNames = participants.map(p => p.name.trim());
  const allItemsAssigned = items.every(item => item.selected.length > 0);
  const total = items.reduce((acc, item) => acc + item.price, 0);

  function handleCheckboxChange(index, name) {
    const updated = [...items];
    if (updated[index].selected.includes(name)) updated[index].selected = updated[index].selected.filter(n => n !== name);
    else updated[index].selected.push(name);
    setItems(updated);
  }
  function handleSelectAllChange(index, checked) { const updated = [...items]; updated[index].selected = checked ? [...selectedNames] : []; setItems(updated); }
  function handleEditPrice(index) {
    const newPrice = prompt('Enter the new price:', items[index].price);
    if (newPrice && !isNaN(newPrice)) { const updated = [...items]; updated[index].price = parseFloat(newPrice); setItems(updated); }
  }
  function handleAddItem() {
    const itemName = prompt('Enter item name:'); const itemPrice = prompt('Enter item price:');
    if (itemName && itemPrice && !isNaN(itemPrice)) setItems([...items, { item: itemName, price: parseFloat(itemPrice), selected: [] }]);
  }
  function handleRemoveItem(index) { setItems(items.filter((_, i) => i !== index)); }
  function handleCalculate() {
    const totalPerPerson = {};
    selectedNames.forEach(name => (totalPerPerson[name] = 0));
    items.forEach(item => {
      const people = item.selected || [];
      if (!people.length) return;
      const splitCost = item.price / people.length;
      people.forEach(name => { totalPerPerson[name] += splitCost; });
    });
    onNext({ splitResult: totalPerPerson });
  }

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginBottom: 8, marginTop: 16 }}><Steps current={2} /></div>
      <div className="w-full max-w-4xl bg-white p-6 rounded-2xl shadow-xl" style={{ border: '1px solid #BFDBFE' }}>
        <h2 className="text-2xl font-semibold mb-1 text-center text-gray-800">Assign Items</h2>
        <p className="text-sm text-gray-400 text-center mb-4">Check who ordered each item</p>
        <div className="overflow-x-auto overflow-y-auto max-h-[400px] scroll-container">
          <table className="min-w-full border border-gray-300 rounded-lg text-center">
            <thead className="sticky top-0 bg-blue-100 z-10">
              <tr className="text-blue-800">
                <th className="p-2 font-semibold min-w-[160px] text-left pl-3">Item</th>
                <th className="p-2 font-semibold min-w-[80px]">Price</th>
                <th className="p-2 font-semibold">All</th>
                {participants.map((p, i) => (
                  <th key={i} className="p-2 font-semibold min-w-[100px]">
                    {p.name || `Person ${i + 1}`}
                    <div style={{ fontSize: 10, fontWeight: 400, color: '#6B7280' }}>{p.method === 'dash' ? '⚡' : p.method === 'bank' ? '🏦' : p.method === 'usdc' ? '🔵' : p.method === 'usdt' ? '🟢' : '💵'}</div>
                  </th>
                ))}
                <th className="p-2 font-semibold">Edit</th>
                <th className="p-2 font-semibold">Remove</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b hover:bg-blue-50 transition">
                  <td className="p-2 text-gray-700 text-left pl-3 break-words">{item.item}</td>
                  <td className="p-2 text-gray-700">{currency}{item.price.toFixed(2)}</td>
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={item.selected.length === selectedNames.length && selectedNames.length > 0}
                      onChange={e => handleSelectAllChange(index, e.target.checked)} className="accent-blue-500" />
                  </td>
                  {participants.map((p, i) => (
                    <td key={i} className="p-2 text-center">
                      <input type="checkbox" checked={item.selected.includes(p.name.trim())}
                        onChange={() => handleCheckboxChange(index, p.name.trim())} className="accent-blue-500" />
                    </td>
                  ))}
                  <td className="p-2"><button onClick={() => handleEditPrice(index)} className="text-blue-500 text-sm underline">Edit</button></td>
                  <td className="p-2"><button onClick={() => handleRemoveItem(index)} className="text-red-500 text-sm underline">Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={handleAddItem} className="py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition text-sm">+ Add Item</button>
        </div>
        <div className="text-center mt-4">
          <h3 className="text-lg font-semibold mb-4">Total: <span className="text-blue-600">{currency}{total.toFixed(2)}</span></h3>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={onBack} className="py-2 px-6 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300 transition">← Back</button>
            <button onClick={handleCalculate} disabled={!allItemsAssigned}
              className={`py-2 px-8 ${allItemsAssigned ? 'bg-blue-600 hover:bg-blue-800' : 'bg-gray-400'} text-white font-semibold rounded-md transition`}>
              Calculate Split →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE 3
// ═══════════════════════════════════════════════════════════════════
function Page3({ splitResult, participants, onBack, onHome, onSettle }) {
  const [currency] = useState('RM');
  const [explorerUrl, setExplorerUrl] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [paidStatus, setPaidStatus] = useState({});
  const [saving, setSaving] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [settled, setSettled] = useState(false);

  const totalPeople = Object.keys(splitResult).length;
  const paidCount = Object.values(paidStatus).filter(Boolean).length;
  const allPaid = totalPeople > 0 && paidCount === totalPeople;
  const dcEarned = participants.reduce((sum, p) => sum + (['dash','usdc','usdt'].includes(p.method) ? 2 : 1), 0) + 5;

  const hasSaved = React.useRef(false);
  const hasUpdatedStatus = React.useRef(false);
  const hasCalledSettle = React.useRef(false);

  React.useEffect(() => {
    if (hasSaved.current) return;
    hasSaved.current = true;
    const timeout = setTimeout(() => { setExplorerUrl(CONTRACT_EXPLORER); setSaving(false); }, 8000);
    saveSplit({
      participants: participants.map(p => ({ name: p.name, paymentMethod: p.method, amount: splitResult[p.name.trim()] || 0, dashAddress: p.method === 'dash' ? MOCK_DASH_ADDRESS : (p.method === 'usdc' || p.method === 'usdt') ? `swap-${p.method}` : null })),
      totalAmount: Object.values(splitResult).reduce((a, b) => a + b, 0),
      merchant: 'Restaurant', date: new Date().toISOString(),
    }).then(saveRes => {
      clearTimeout(timeout);
      setDocumentId(saveRes.documentId || null);
      setExplorerUrl(saveRes.explorerLinks?.document || saveRes.explorerLinks?.identity || CONTRACT_EXPLORER);
      setSaving(false);
    }).catch(() => { clearTimeout(timeout); setExplorerUrl(CONTRACT_EXPLORER); setSaving(false); });
    return () => clearTimeout(timeout);
  }, []);

  React.useEffect(() => {
    if (!allPaid || hasUpdatedStatus.current || !documentId) return;
    hasUpdatedStatus.current = true;
    setStatusUpdating(true);
    axios.post(`${BACKEND}/update-split-status`, { documentId }).finally(() => setStatusUpdating(false));
  }, [allPaid, documentId]);

  React.useEffect(() => {
    if (!allPaid || hasCalledSettle.current) return;
    hasCalledSettle.current = true;
    setSettled(true);
    onSettle?.();
  }, [allPaid, onSettle]);

  // Poll Dash Platform every 5s to auto-update when friend pays remotely
  React.useEffect(() => {
    if (allPaid || !documentId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND}/split-history`);
        if (res.data.success) {
          const record = res.data.history.find(h => h.id === documentId);
          if (record?.paymentStatus === 'settled') {
            const allPaidStatus = {};
            Object.keys(splitResult).forEach(name => { allPaidStatus[name] = true; });
            setPaidStatus(allPaidStatus);
          }
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [documentId, allPaid, splitResult]);

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onHome} />
      </div>
      <div style={{ marginBottom: 8 }}><Steps current={3} /></div>
      <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-xl" style={{ border: '1px solid #BFDBFE' }}>
        <h2 className="text-2xl font-semibold mb-3 text-blue-700 text-center">Split Result</h2>
        {!allPaid && (
          <div style={{ background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)', border: '1px solid #BFDBFE', borderRadius: 10, padding: '10px 14px', marginBottom: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 600 }}>🌸 Settle this split to earn <strong>+{dcEarned} DC</strong> and bloom your flower!</div>
          </div>
        )}
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px', marginBottom: 16, textAlign: 'center' }}>
          {saving ? <span style={{ fontSize: 13, color: '#1D4ED8' }}>⏳ Saving to Dash Platform...</span>
            : statusUpdating ? <span style={{ fontSize: 13, color: '#1D4ED8' }}>⏳ Updating status on Dash Platform...</span>
            : <span style={{ fontSize: 13, color: '#1D4ED8' }}>✓ {allPaid ? 'Settled' : 'Saved'} on Dash Platform — <a href={explorerUrl} target="_blank" rel="noreferrer" style={{ color: '#1BA2F6', textDecoration: 'underline', fontWeight: 600 }}>View on Explorer</a></span>}
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
            <span>Payments settled</span><span>{paidCount} / {totalPeople}</span>
          </div>
          <div style={{ background: '#E5E7EB', borderRadius: 999, height: 8, overflow: 'hidden' }}>
            <div style={{ background: allPaid ? '#2563EB' : '#1BA2F6', height: '100%', borderRadius: 999, width: `${totalPeople > 0 ? (paidCount / totalPeople) * 100 : 0}%`, transition: 'width 0.4s ease' }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(splitResult).map(([name, amount]) => {
            const p = participants.find(p => p.name.trim() === name) || {};
            const isPaid = paidStatus[name];
            const dcForThis = ['dash','usdc','usdt'].includes(p.method) ? 2 : 1;
            return (
              <div key={name} style={{ border: `1px solid ${isPaid ? '#BFDBFE' : '#E5E7EB'}`, borderRadius: 10, padding: '12px 14px', background: isPaid ? '#EFF6FF' : 'white', transition: 'all 0.3s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{name}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                      {p.method === 'dash' ? '⚡ Dash' : p.method === 'bank' ? '🏦 Bank Transfer' : p.method === 'usdc' ? '🔵 USDC → Dash swap' : p.method === 'usdt' ? '🟢 USDT → Dash swap' : '💵 Cash'}
                      {' · '}<span style={{ color: '#1BA2F6', fontWeight: 600 }}>+{dcForThis} 🪙 DC</span>
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#2563EB' }}>{currency}{amount.toFixed(2)}</div>
                </div>
                {p.method === 'dash' && (
                  <div style={{ background: '#EFF6FF', borderRadius: 6, padding: '6px 10px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: '#1E40AF', fontFamily: 'monospace' }}>{MOCK_DASH_ADDRESS}</span>
                    <button onClick={() => navigator.clipboard.writeText(MOCK_DASH_ADDRESS)} style={{ fontSize: 11, color: '#1BA2F6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Copy</button>
                  </div>
                )}
                {p.method === 'bank' && (p.bankName || p.bankAccount) && (
                  <div style={{ background: '#F3F4F6', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12, color: '#374151' }}>
                    🏦 {p.bankName || '—'} · {p.bankAccount || 'No account entered'}
                  </div>
                )}
                {!isPaid ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                    <ActionButton participant={p} amount={amount} currency={currency} onPaid={() => setPaidStatus(prev => ({ ...prev, [name]: true }))} />
                    <SendLinkButton participant={p} amount={amount} merchant="Restaurant" />
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: '#2563EB', fontWeight: 700 }}>✓ {p.method === 'dash' ? 'Sent!' : 'Paid!'}</span>
                )}
              </div>
            );
          })}
        </div>
        {allPaid && (
          <div style={{ marginTop: 20, background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)', border: '2px solid #2563EB', borderRadius: 12, padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>🌸</div>
            <div style={{ fontWeight: 800, fontSize: 20, color: '#1D4ED8' }}>All settled!</div>
            <div style={{ fontSize: 13, color: '#2563EB', marginTop: 4 }}>Split recorded on Dash Platform.</div>
            <div style={{ fontSize: 13, color: '#10B981', fontWeight: 700, marginTop: 6 }}>🪙 +{dcEarned} DC earned — your flower just grew!</div>
            {explorerUrl && <a href={explorerUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 10, fontSize: 12, color: '#1BA2F6', textDecoration: 'underline' }}>🔗 View on Dash Explorer</a>}
          </div>
        )}
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button onClick={onHome} className="py-2 px-6 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300 transition text-sm">← Back to Home</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAY LINK PAGE — friend opens this from WhatsApp, no app needed
// ═══════════════════════════════════════════════════════════════════
function PayLinkPage() {
  const params = new URLSearchParams(window.location.search);
  const name       = params.get('name')   || 'Friend';
  const amount     = parseFloat(params.get('amount') || '0');
  const method     = params.get('method') || 'dash';
  const merchant   = params.get('merchant') || 'Restaurant';
  const currency   = 'RM';

  const [paid, setPaid] = useState(false);

  const methodIcon  = method === 'dash' ? '⚡' : method === 'usdc' ? '🔵' : method === 'usdt' ? '🟢' : method === 'bank' ? '🏦' : '💵';
  const methodLabel = method === 'dash' ? 'Dash' : method === 'usdc' ? 'USDC' : method === 'usdt' ? 'USDT' : method === 'bank' ? 'Bank Transfer' : 'Cash';

  const participant = { name, method, bankName: params.get('bankName') || '', bankAccount: params.get('bankAccount') || '' };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #EFF6FF 0%, #F0F9FF 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 16px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
          <img src={dashLogo} alt="Dash" style={{ height: 36, objectFit: 'contain' }} />
          <span style={{ fontSize: 28, fontWeight: 900, letterSpacing: -1, fontFamily: "'Syne', sans-serif", color: '#1BA2F6' }}>Split</span>
        </div>
        <div style={{ fontSize: 12, color: '#94A3B8', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 500 }}>Payment Request</div>
      </div>

      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Bill card */}
        <div style={{ background: 'linear-gradient(135deg, #0C44AC, #1BA2F6)', borderRadius: 20, padding: '24px 22px', color: 'white', marginBottom: 20, boxShadow: '0 8px 32px rgba(12,68,172,0.28)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -24, right: -24, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
          <div style={{ fontSize: 11, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Your share — {merchant}</div>
          <div style={{ fontSize: 44, fontWeight: 900, fontFamily: "'Syne', sans-serif", marginBottom: 4 }}>{currency} {amount.toFixed(2)}</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '5px 14px', fontSize: 13, fontWeight: 700 }}>
            {methodIcon} Pay with {methodLabel}
          </div>
        </div>

        {/* Greeting */}
        <div style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 16, padding: '16px 18px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ fontSize: 32 }}>👋</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>Hey {name}!</div>
            <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>Your share for <strong>{merchant}</strong> is ready to pay. No app needed.</div>
          </div>
        </div>

        {/* Payment panel */}
        {!paid ? (
          <div style={{ background: 'white', border: '1px solid #BFDBFE', borderRadius: 16, padding: '18px', marginBottom: 16 }}>
            <ActionButton participant={participant} amount={amount} currency={currency} onPaid={() => setPaid(true)} />
          </div>
        ) : (
          <div style={{ background: 'linear-gradient(135deg, #ECFDF5, #D1FAE5)', border: '2px solid #10B981', borderRadius: 16, padding: '24px', textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 20, color: '#059669' }}>Payment sent!</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 6 }}>The organiser has been notified. You're all set!</div>
            <div style={{ fontSize: 12, color: '#1BA2F6', fontWeight: 700, marginTop: 8 }}>+2 🪙 DC earned on your account</div>
          </div>
        )}

        {/* Powered by */}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <div style={{ fontSize: 11, color: '#CBD5E1', letterSpacing: 0.5 }}>Powered by Dash · Secured on blockchain</div>
          <div style={{ fontSize: 11, color: '#CBD5E1', marginTop: 4 }}>
            Want to split bills too? <a href="/" style={{ color: '#1BA2F6', textDecoration: 'underline' }}>Try DashSplit free</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VOUCHER PAGE
// ═══════════════════════════════════════════════════════════════════
function VoucherPage({ onHome, onThemeUnlock, unlockedThemes = [], currentTheme, onThemeChange, coins, onSpendCoins }) {
  const [redeemed, setRedeemed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dash_redeemed') || '{}'); } catch { return {}; }
  });
  const [toast, setToast] = useState(null);
  const [localCoins, setLocalCoins] = useState(coins);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3500); }

  function handleRedeem(voucher) {
    if (localCoins < voucher.cost || redeemed[voucher.id]) return;
    const newCoins = localCoins - voucher.cost;
    setLocalCoins(newCoins);
    const newRedeemed = { ...redeemed, [voucher.id]: true };
    setRedeemed(newRedeemed);
    localStorage.setItem('dash_redeemed', JSON.stringify(newRedeemed));
    onSpendCoins?.(voucher.cost);
    showToast(`🎉 "${voucher.deal}" at ${voucher.restaurant} redeemed!`);
  }

  return (
    <div className="min-h-screen dash-bg flex flex-col items-center py-8">
      <Logo />
      {toast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#1D4ED8', color: 'white', borderRadius: 10, padding: '12px 20px', fontWeight: 600, fontSize: 13, zIndex: 999, boxShadow: '0 4px 20px rgba(29,78,216,0.4)', whiteSpace: 'nowrap' }}>
          {toast}
        </div>
      )}
      <div style={{ marginTop: 20, width: '100%', maxWidth: 480 }}>
        <TopBar onBack={onHome} coins={localCoins} />
      </div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#64748B', marginBottom: 6, fontWeight: 500 }}>Your balance</div>
        <CoinBadge coins={localCoins} size="lg" />
        <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 6 }}>🌸 {getStage(localCoins).name} garden</div>
      </div>
      <div style={{ width: '100%', maxWidth: 480, padding: '0 16px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E3A5F', marginBottom: 4 }}>Restaurant Vouchers</h2>
        <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 16 }}>Exclusive deals from our restaurant partners. Redeem with your 🪙 DC.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {VOUCHERS.map(v => {
            const isRedeemed = redeemed[v.id];
            const canAfford = localCoins >= v.cost;
            return (
              <div key={v.id} style={{ background: isRedeemed ? '#F1F5F9' : v.color, border: `1px solid ${isRedeemed ? '#CBD5E1' : v.border}`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, opacity: isRedeemed ? 0.7 : 1, transition: 'all 0.2s' }}>
                <div style={{ fontSize: 32, lineHeight: 1 }}>{v.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1E293B' }}>{v.restaurant}</span>
                    {v.tag && !isRedeemed && <span style={{ fontSize: 10, fontWeight: 700, background: '#1BA2F6', color: 'white', borderRadius: 999, padding: '2px 7px' }}>{v.tag}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>{v.deal}</div>
                  <CoinBadge coins={v.cost} />
                </div>
                <button onClick={() => handleRedeem(v)} disabled={isRedeemed || !canAfford}
                  style={{ background: isRedeemed ? '#94A3B8' : canAfford ? 'linear-gradient(135deg, #1BA2F6, #0C44AC)' : '#CBD5E1', color: 'white', border: 'none', borderRadius: 9, padding: '8px 14px', fontWeight: 700, fontSize: 12, cursor: isRedeemed || !canAfford ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', minWidth: 80 }}>
                  {isRedeemed ? '✓ Used' : !canAfford ? 'Need more' : 'Redeem'}
                </button>
              </div>
            );
          })}
        </div>
        {Object.values(redeemed).some(Boolean) && (
          <div style={{ marginTop: 20, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1D4ED8', marginBottom: 8 }}>✓ Your Redeemed Vouchers</div>
            {VOUCHERS.filter(v => redeemed[v.id]).map(v => (
              <div key={v.id} style={{ fontSize: 13, color: '#374151', padding: '4px 0', borderBottom: '1px solid #DBEAFE' }}>
                {v.icon} <strong>{v.restaurant}</strong> — {v.deal}
              </div>
            ))}
          </div>
        )}
        <div style={{ textAlign: 'center', marginTop: 24, paddingBottom: 32 }}>
          <button onClick={onHome} className="py-2 px-6 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300 transition text-sm">← Back to Home</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DEV MOCK FLOW PANEL
// ═══════════════════════════════════════════════════════════════════
function MockFlowPanel({ coins, onAddCoins, onReset }) {
  const [open, setOpen] = useState(false);
  const stage = getStage(coins);
  return (
    <div style={{ position: 'fixed', bottom: 20, right: 16, zIndex: 9999, fontFamily: 'monospace', fontSize: 12 }}>
      {open && (
        <div style={{ background: '#0F172A', color: '#E2E8F0', borderRadius: 14, padding: '16px 18px', marginBottom: 10, width: 240, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: '1px solid #334155' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#38BDF8', marginBottom: 12 }}>🧪 Mock Flow Tester</div>
          <div style={{ color: '#94A3B8', marginBottom: 4 }}>Current state</div>
          <div style={{ background: '#1E293B', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
            <div>🌸 <strong style={{ color: '#F0ABFC' }}>{stage.name}</strong></div>
            <div>🪙 <strong style={{ color: '#FCD34D' }}>{coins} DC</strong></div>
            <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>{stage.petals} petals bloomed</div>
          </div>
          <div style={{ color: '#94A3B8', marginBottom: 6 }}>Add DashCoins</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {[2, 10, 30, 50].map(n => (
              <button key={n} onClick={() => onAddCoins(n)} style={{ background: '#1E3A5F', color: '#93C5FD', border: '1px solid #2563EB', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: 700 }}>+{n}</button>
            ))}
          </div>
          <div style={{ color: '#94A3B8', marginBottom: 6 }}>Simulate events</div>
          <button onClick={() => onAddCoins(8)} style={{ width: '100%', background: '#1BA2F6', color: 'white', border: 'none', borderRadius: 8, padding: '7px', cursor: 'pointer', fontWeight: 700, marginBottom: 6, fontFamily: 'monospace' }}>⚡ Settle split (+8 DC)</button>
          <button onClick={() => onAddCoins(2)} style={{ width: '100%', background: '#7C3AED', color: 'white', border: 'none', borderRadius: 8, padding: '7px', cursor: 'pointer', fontWeight: 700, marginBottom: 10, fontFamily: 'monospace' }}>⚡ Dash pay (+2 DC)</button>
          <div style={{ borderTop: '1px solid #334155', paddingTop: 10 }}>
            <button onClick={onReset} style={{ width: '100%', background: '#7F1D1D', color: '#FCA5A5', border: '1px solid #DC2626', borderRadius: 8, padding: '6px', cursor: 'pointer', fontWeight: 700, fontFamily: 'monospace' }}>🗑 Reset all data</button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen(o => !o)} style={{ background: open ? '#0F172A' : '#1BA2F6', color: 'white', border: 'none', borderRadius: 999, padding: '10px 16px', cursor: 'pointer', fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        🧪 {open ? 'Close' : 'Test'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN APP ROUTER
// ═══════════════════════════════════════════════════════════════════
function App() {
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('paylink') === '1' ? 'paylink' : 'home';
  });
  const [data, setData] = useState({});
  const [coins, setCoins] = useState(() => loadCoins());
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('dash_theme') || 'default');
  const [unlockedThemes, setUnlockedThemes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dash_unlocked_themes') || '[]'); } catch { return []; }
  });
  const [levelUpStage, setLevelUpStage] = useState(null);

  function addCoins(amount) {
    setCoins(prev => {
      const prevStage = getStageIndex(prev);
      const next = prev + amount;
      const nextStageIdx = getStageIndex(next);
      saveCoins(next);
      if (nextStageIdx > prevStage) setLevelUpStage(GARDEN_STAGES[nextStageIdx]);
      return next;
    });
  }

  function spendCoins(amount) {
    setCoins(prev => { const n = Math.max(0, prev - amount); saveCoins(n); return n; });
  }

  function handleThemeUnlock(themeKey) {
    const updated = [...unlockedThemes, themeKey];
    setUnlockedThemes(updated);
    localStorage.setItem('dash_unlocked_themes', JSON.stringify(updated));
  }

  function handleThemeChange(themeKey) {
    setCurrentTheme(themeKey);
    localStorage.setItem('dash_theme', themeKey);
  }

  function handleSettle(participants) {
    const perParticipant = (participants || []).reduce((sum, p) => sum + (p.method === 'dash' ? 2 : 1), 0);
    addCoins(perParticipant + 5);
  }

  function resetAll() {
    if (!window.confirm('Reset all garden progress, coins, and redeemed vouchers?')) return;
    localStorage.removeItem('dash_coins');
    localStorage.removeItem('dash_theme');
    localStorage.removeItem('dash_unlocked_themes');
    localStorage.removeItem('dash_redeemed');
    localStorage.removeItem('dash_wallet');
    setCoins(INITIAL_MOCK_COINS);
    setCurrentTheme('default');
    setUnlockedThemes([]);
  }

  function goHome() { setData({}); setPage('home'); }
  function goToSplit1({ items, participants }) { setData(prev => ({ ...prev, items, participants })); setPage('split2'); }
  function goToSplit2({ splitResult }) { setData(prev => ({ ...prev, splitResult })); setPage('split3'); }
  function goToDetail(record) { setData(prev => ({ ...prev, historyRecord: record })); setPage('history-detail'); }

  return (
    <>
      <style>{`
        @keyframes flowerSway { 0% { transform: rotate(-6deg); } 100% { transform: rotate(6deg); } }
        @keyframes leafSway { 0% { transform: rotate(-4deg); } 100% { transform: rotate(2deg); } }
        @keyframes leafSwayR { 0% { transform: rotate(3deg); } 100% { transform: rotate(-3deg); } }
        @keyframes sparkle { from { opacity: 0.4; transform: scale(0.8); } to { opacity: 1; transform: scale(1.3); } }
        @keyframes slideDown { from { opacity: 0; transform: translateX(-50%) translateY(-12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {levelUpStage && <LevelUpToast stage={levelUpStage} onDismiss={() => setLevelUpStage(null)} />}
      <MockFlowPanel coins={coins} onAddCoins={addCoins} onReset={resetAll} />

      {page === 'paylink'        && <PayLinkPage />}
      {page === 'home'           && <HomePage onSplitBill={() => setPage('split1')} onVouchers={() => setPage('vouchers')} onHistory={() => setPage('history')} onQR={() => setPage('qrcode')} onWallet={() => setPage('wallet')} currentTheme={currentTheme} coins={coins} unlockedThemes={unlockedThemes} onThemeChange={handleThemeChange} />}
      {page === 'split1'         && <Page1 onNext={goToSplit1} onHome={goHome} onHistory={() => setPage('history')} coins={coins} />}
      {page === 'split2'         && <Page2 items={data.items} participants={data.participants} onNext={goToSplit2} onBack={() => setPage('split1')} />}
      {page === 'split3'         && <Page3 splitResult={data.splitResult} participants={data.participants} onBack={() => setPage('split2')} onHome={goHome} onSettle={() => handleSettle(data.participants)} />}
      {page === 'vouchers'       && <VoucherPage onHome={goHome} onThemeUnlock={handleThemeUnlock} unlockedThemes={unlockedThemes} currentTheme={currentTheme} onThemeChange={handleThemeChange} coins={coins} onSpendCoins={spendCoins} />}
      {page === 'history'        && <HistoryPage onHome={goHome} onDetail={goToDetail} coins={coins} />}
      {page === 'history-detail' && <HistoryDetailPage record={data.historyRecord} onBack={() => setPage('history')} coins={coins} />}
      {page === 'qrcode'         && <QRCodePage onHome={goHome} coins={coins} />}
      {page === 'wallet'         && <WalletPage onHome={goHome} coins={coins} />}
    </>
  );
}

export default App;